import { useState } from 'react';
import { Button, Group, Popover, Stack, Text } from '@mantine/core';
import { Trash2 } from 'react-feather';
import { deleteSessionTranscript } from '../../../../services/api/coachPostSession';

/**
 * MED-1187 (AC8, folded from MED-1186) — the coach/leader-facing manual
 * "delete for sure" path for the persisted session transcript.
 *
 * Renders only when a transcript actually exists (`hasTranscript` from the
 * post-session bundle). Delete is guarded by an in-app Popover confirm — never
 * a browser-native `confirm()` (project pattern: native modals block
 * automation/UX). On success the control flips to a quiet "deleted" line; the
 * server delete is idempotent and ownership-guarded (group coach or the
 * designated session leader), so a stale click can't hurt.
 *
 * Deleting the transcript keeps the airtime result (a stored, de-identified
 * aggregate) but conversation-based TAKEAWAYS do not survive a regenerate:
 * the next recap compose (hash miss) runs without the transcript and produces
 * empty takeaway bullets — the dialog copy says so honestly.
 */

const LABEL_COLOR = '#6B7280';

interface Props {
  sessionId: string;
  /** From the bundle (MED-1187 `has_transcript`) — control is absent when false. */
  hasTranscript: boolean;
}

export function DeleteTranscriptControl({ sessionId, hasTranscript }: Props) {
  const [opened, setOpened] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!hasTranscript && !deleted) return null;

  if (deleted) {
    return (
      <Text fz={12} c={LABEL_COLOR} data-testid="transcript-deleted-note">
        Session transcript deleted.
      </Text>
    );
  }

  const onConfirm = async () => {
    setDeleting(true);
    setFailed(false);
    try {
      await deleteSessionTranscript(sessionId);
      setDeleted(true);
      setOpened(false);
    } catch {
      setFailed(true);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Group justify="flex-end" gap={8}>
      {failed && (
        <Text fz={12} c="red">
          Couldn&apos;t delete — try again.
        </Text>
      )}
      <Popover
        opened={opened}
        onChange={setOpened}
        position="top-end"
        withArrow
        shadow="md"
        width={280}
      >
        <Popover.Target>
          <Button
            size="xs"
            variant="subtle"
            color="red"
            leftSection={<Trash2 size={13} />}
            onClick={() => setOpened(o => !o)}
            data-testid="delete-transcript-button"
          >
            Delete transcript
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap={8}>
            <Text fz={13} fw={600}>
              Delete this session&apos;s transcript?
            </Text>
            <Text fz={12} c={LABEL_COLOR}>
              This permanently removes the stored conversation. Takeaways drawn
              from it will disappear from newly generated recap drafts.
            </Text>
            <Group justify="flex-end" gap={8}>
              <Button size="xs" variant="default" onClick={() => setOpened(false)}>
                Cancel
              </Button>
              <Button
                size="xs"
                color="red"
                loading={deleting}
                onClick={onConfirm}
                data-testid="delete-transcript-confirm"
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}

export default DeleteTranscriptControl;
